import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';

interface DeleteGameConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  gameName: string;
  gameType: string;
  companyName: string;
}

const DeleteGameConfirmationModal: React.FC<DeleteGameConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  gameName,
  gameType,
  companyName
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [hasTypedCorrectly, setHasTypedCorrectly] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const expectedText = `DELETE ${gameName}`;

  useEffect(() => {
    setHasTypedCorrectly(confirmationText === expectedText);
  }, [confirmationText, expectedText]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      gsap.set([overlayRef.current, modalRef.current], { 
        opacity: 0,
        visibility: 'visible'
      });
      
      gsap.set(modalRef.current, { scale: 0.9, y: 50 });
      
      gsap.to(overlayRef.current, {
        opacity: 1,
        duration: 0.2
      });
      
      gsap.to(modalRef.current, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
        delay: 0.1
      });
    } else {
      document.body.style.overflow = '';
      
      if (modalRef.current && overlayRef.current) {
        gsap.to(modalRef.current, {
          opacity: 0,
          scale: 0.9,
          y: 50,
          duration: 0.2,
          ease: 'power2.in'
        });
        
        gsap.to(overlayRef.current, {
          opacity: 0,
          duration: 0.2,
          delay: 0.1,
          onComplete: () => {
            gsap.set([overlayRef.current, modalRef.current], { 
              visibility: 'hidden' 
            });
          }
        });
      }
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    if (!loading) {
      setConfirmationText('');
      setHasTypedCorrectly(false);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (hasTypedCorrectly && !loading) {
      onConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        style={{ visibility: 'hidden' }}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-red-200 dark:border-red-800 w-full max-w-md"
          style={{ visibility: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-red-200 dark:border-red-800">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                Delete Game
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-red-600 dark:text-red-400" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Warning Icon and Message */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  This action cannot be undone
                </h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  You are about to permanently delete the following game:
                </p>
              </div>
            </div>

            {/* Game Details */}
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Game:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{gameName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Type:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{gameType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Company:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">{companyName}</span>
                </div>
              </div>
            </div>

            {/* Consequences */}
            <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <h5 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                ⚠️ Consequences of deletion:
              </h5>
              <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                <li>• All registration data will be lost</li>
                <li>• Download history will be removed</li>
                <li>• Analytics data will be deleted</li>
                <li>• This action cannot be reversed</li>
              </ul>
            </div>

            {/* Confirmation Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To confirm deletion, type <code className="bg-gray-100 dark:bg-dark-700 px-1 py-0.5 rounded text-red-600 dark:text-red-400 font-mono text-xs">{expectedText}</code> below:
              </label>
              <input
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                disabled={loading}
                className={`input-field font-mono ${hasTypedCorrectly ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : 'border-red-500 focus:border-red-500 focus:ring-red-500'}`}
                placeholder={expectedText}
                autoComplete="off"
                spellCheck={false}
              />
              {confirmationText && !hasTypedCorrectly && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  Text does not match. Please type exactly: {expectedText}
                </p>
              )}
              {hasTypedCorrectly && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                  ✓ Confirmation text matches
                </p>
              )}
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400">
              This action requires the <code className="bg-gray-100 dark:bg-dark-700 px-1 py-0.5 rounded font-mono">delete:games</code> permission.
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/5">
            <button
              onClick={handleClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !hasTypedCorrectly}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="small" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete Game Permanently
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteGameConfirmationModal;
